import type { CasinoFmGame } from "./casinoGameSearchUtils";
import { filterGamesByQuery } from "./casinoGameSearchUtils";

export type GamesGroupSortMode = "provider" | "subprovider";

export type CasinoConfigGameRow = CasinoFmGame & {
  is_active?: boolean;
  custom_image?: string;
  image?: string;
  external_image_url?: string;
  thumbnail?: string;
  /** Shown in the GAME ID column (e.g. `2851:20557`) while `game_id` stays unique for keys */
  display_game_id?: string;
  game_type?: string;
  category?: string;
  tags?: string[];
  aggregator?: boolean;
};

/** Precedence mirrors the public catalog, so an admin-uploaded `custom_image` wins over provider defaults. */
export function gameIconUrl(game: CasinoConfigGameRow): string {
  return String(
    game.custom_image ||
      game.image ||
      game.external_image_url ||
      game.thumbnail ||
      "",
  );
}

export function getCasinoConfigGameKey(game: CasinoConfigGameRow): string {
  const p = String(game?.provider ?? "");
  const id = String(game?.game_id ?? game?.id ?? "");
  return `${p}_${id}`;
}

export function getGroupKeyAndTitle(
  game: CasinoConfigGameRow,
  mode: GamesGroupSortMode,
): { key: string; title: string } {
  const sub = String(game.sub ?? "").trim();
  const prov = String(game.provider ?? "").trim();
  const provName = String(game.provider_name ?? game.provider ?? "").trim();

  if (mode === "provider") {
    const key = `${sub}::${prov}`;
    const title =
      provName && sub
        ? `${provName} / ${sub}`
        : provName || sub || prov || "—";
    return { key, title };
  }

  const key = `${prov}::${sub}`;
  const title =
    sub && provName
      ? `${sub} / ${provName}`
      : provName || sub || prov || "—";
  return { key, title };
}

export type GameGroupMeta = {
  key: string;
  title: string;
  games: CasinoConfigGameRow[];
  provider?: string;
  sub?: string;
  activeCount: number;
  total: number;
  /** When tree search is applied, API may return how many games in the group match (for badge denominator). */
  matchedTotal?: number | null;
  /** Denominator for active/total badge (uses matched_total when search narrows the group). */
  displayTotal: number;
  status: "mixed" | "all_on" | "off";
};

export function buildGameGroups(
  games: CasinoConfigGameRow[],
  mode: GamesGroupSortMode,
): GameGroupMeta[] {
  const map = new Map<string, { title: string; games: CasinoConfigGameRow[] }>();

  for (const game of games) {
    const { key, title } = getGroupKeyAndTitle(game, mode);
    const prev = map.get(key);
    if (!prev) {
      map.set(key, { title, games: [game] });
    } else {
      prev.games.push(game);
    }
  }

  const rows: GameGroupMeta[] = Array.from(map.entries()).map(([key, v]) => {
    const total = v.games.length;
    const activeCount = v.games.filter((g) => g.is_active !== false).length;
    let status: GameGroupMeta["status"] = "mixed";
    if (activeCount === 0) status = "off";
    else if (activeCount === total) status = "all_on";
    return {
      key,
      title: v.title,
      games: v.games,
      activeCount,
      total,
      displayTotal: total,
      status,
    };
  });

  return rows.sort((a, b) => a.title.localeCompare(b.title));
}

export function filterCasinoConfigGames(
  games: CasinoConfigGameRow[],
  query: string,
): CasinoConfigGameRow[] {
  return filterGamesByQuery(games, query) as CasinoConfigGameRow[];
}

export function formatGameIdForDisplay(game: CasinoConfigGameRow): string {
  if (game.display_game_id) return String(game.display_game_id);
  return String(game.game_id ?? game.id ?? "—");
}

export function formatGameIdForModalSubtitle(game: CasinoConfigGameRow): string {
  const prov = String(game.provider ?? "").toLowerCase();
  const id = String(game.game_id ?? game.id ?? "");
  return prov && id ? `${prov}/${id}` : id || "—";
}
